/*
 * Row ranks for layered auto-layouts. Both canvases lay a flow out left to right and decide first
 * which row each node belongs on — a branch above or below the node it leaves, a join back on its
 * split's row — then seed ELK with those rows (`elk.position`) so it keeps them in that order.
 * What counts as a branch differs per canvas (gateway branches, SUCCESS/FAIL exits, fork
 * conditions…); turning rows into ranks does not.
 */

/** One step of the flow: from a node to the next, `offset` rows above (negative) or below it. */
export interface RowStep<Id> {
  from: Id
  to: Id
  offset: number
}

/**
 * Seed distance between two rows in `elk.position`: far above any real coordinate, so ranks, not
 * the old drawing, decide a column's order.
 */
export const RANK_SEED_STEP = 1_000

/** Straightness priority of the edges that carry a row on (`elk.layered.priority.straightness`). */
export const ROW_STRAIGHTNESS = '10'

/** `items` fanned symmetrically around their centre: the first in the middle, then alternately above and below. */
export const fanOrder = <T>(items: T[]): T[] =>
  items.reduce<T[]>((fan, item, index) => (index % 2 === 1 ? [item, ...fan] : [...fan, item]), [])

/** The offset from the centre of the `index`th of `count` rows fanned around it (see fanOrder). */
export const centredOffset = (index: number, count: number): number => index - (count - 1) / 2

/**
 * How far above (negative) or below (positive) the start's row each node reachable from
 * `startIds` belongs, in rows: the sum of the step offsets along its path — taking the path that
 * strays least, so a join lands back on its split's row. Nodes no start reaches are left out.
 */
export function rowRanks<Id>(startIds: Id[], steps: RowStep<Id>[]): Map<Id, number> {
  const stepsFrom = steps.reduce(
    (grouped, step) => grouped.set(step.from, [...(grouped.get(step.from) ?? []), step]),
    new Map<Id, RowStep<Id>[]>()
  )
  const best = new Map<Id, { cost: number; rank: number }>(
    startIds.map((id) => [id, { cost: 0, rank: 0 }])
  )
  // Reached but not settled yet; Dijkstra on total deviation, settling the cheapest each round
  const frontier = new Set(startIds)
  const nextToSettle = (): Id | undefined =>
    [...frontier].reduce<Id | undefined>(
      (cheapest, id) =>
        cheapest === undefined || best.get(id).cost < best.get(cheapest).cost ? id : cheapest,
      undefined
    )

  for (let current = nextToSettle(); current !== undefined; current = nextToSettle()) {
    frontier.delete(current)
    const { cost, rank } = best.get(current)
    ;(stepsFrom.get(current) ?? [])
      .filter((step) => (best.get(step.to)?.cost ?? Infinity) > cost + Math.abs(step.offset))
      .forEach((step) => {
        best.set(step.to, { cost: cost + Math.abs(step.offset), rank: rank + step.offset })
        frontier.add(step.to)
      })
  }

  return new Map([...best.entries()].map(([id, { rank }]) => [id, rank]))
}
